import { Building } from "lucide-react";
import { useTranslation } from "react-i18next";
import GenericCrudModule from "@/components/GenericCrudModule";
import OntologyExplorerButton from "@/components/OntologyExplorerButton";
import ContextualImportButton from "@/components/ContextualImportButton";
import ContextButton from "@/components/ContextButton";
import DataStateTrendBadge from "@/components/DataStateTrendBadge";
import ProactiveWhisper from "@/components/ProactiveWhisper";
import ThreeQPageHeader from "@/components/ThreeQPageHeader";

const FOURN_IMPORT_FIELDS = [
  { key: "nom", label: "Nom", required: true },
  { key: "categorie", label: "Catégorie", default: "Autre" },
  { key: "criticite", label: "Criticité", default: "moyenne" },
  { key: "contact", label: "Contact", default: "" },
  { key: "contrat_eur", label: "Contrat (EUR)", type: "int", default: 0 },
  { key: "score_risque", label: "Score risque", type: "int", default: 50 },
  { key: "statut", label: "Statut", default: "actif" },
];

const CRIT_COLORS = { critique: "#E24B4A", haute: "#EF9F27", moyenne: "#378ADD", faible: "#A1A1AA" };
const STATUT_COLORS = { actif: "#1D9E75", termine: "#A1A1AA", litige: "#EF9F27" };

function formatEur(n) {
  if (!n) return "—";
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)} M€`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(0)} k€`;
  return `${n} €`;
}

export default function Fournisseurs() {
  const { t } = useTranslation();
  return (
    <>
      <ProactiveWhisper module="fournisseurs" />
      {/* V23.15.36 — Doctrine 3Q · fournisseur critique le plus exposé */}
      <ThreeQPageHeader endpoint="/workspace/fournisseurs/top-priority" className="mb-4" />
      <GenericCrudModule
      module={{
        path: "fournisseurs",
        title: t("saas.fournisseurs.title"),
        overline: t("saas.fournisseurs.overline"),
        subtitle: t("saas.fournisseurs.subtitle"),
        accent: "#7F77DD", icon: Building, dataTestId: "fournisseurs",
      }}
      fields={[
        { key: "nom", label: t("saas.fournisseurs.field_nom"), required: true, span: 2 },
        { key: "categorie", label: t("saas.fournisseurs.field_categorie"),
          placeholder: t("saas.fournisseurs.field_categorie_ph") },
        { key: "criticite", label: t("saas.fournisseurs.field_criticite"), type: "select", default: "moyenne",
          options: ["critique", "haute", "moyenne", "faible"].map((v) => ({ value: v, label: t(`saas.fournisseurs.criticites.${v}`) })) },
        { key: "contact", label: t("saas.fournisseurs.field_contact") },
        { key: "contrat_eur", label: t("saas.fournisseurs.field_contrat"), type: "number", default: 0 },
        { key: "score_risque", label: t("saas.fournisseurs.field_score"), type: "number", default: 50, max: 100 },
        { key: "statut", label: t("saas.fournisseurs.field_statut"), type: "select", default: "actif",
          options: ["actif", "termine", "litige"].map((v) => ({ value: v, label: t(`saas.fournisseurs.statuts.${v}`) })) },
        { key: "notes", label: t("saas.fournisseurs.field_notes"), type: "textarea", span: 2 },
      ]}
      columns={[
        { key: "nom", label: t("saas.fournisseurs.col_fournisseur") },
        { key: "categorie", label: t("saas.fournisseurs.col_categorie") },
        { key: "criticite", label: t("saas.fournisseurs.col_criticite"), render: (r) => (
          <span className="text-[10px] px-2 py-0.5 rounded-full font-medium"
            style={{ background: `${CRIT_COLORS[r.criticite]}1A`, color: CRIT_COLORS[r.criticite] }}>
            {t(`saas.fournisseurs.criticites.${r.criticite}`, { defaultValue: r.criticite })}
          </span>
        )},
        { key: "contrat_eur", label: t("saas.fournisseurs.col_contrat"), render: (r) => formatEur(r.contrat_eur) },
        { key: "score_risque", label: t("saas.fournisseurs.col_score"), render: (r) => {
          // Polarity positive — higher score = healthier supplier
          const etat = r.score_risque >= 70 ? "bon" : r.score_risque >= 50 ? "attention" : "critique";
          // Heuristic tendance — fallback stable; backend may attach r.tendance later
          const tend = r.tendance === "hausse" || r.tendance === "baisse" ? r.tendance : "stable";
          return <DataStateTrendBadge data={r.score_risque} etat={etat} tendance={tend} polarity="positive" compact />;
        }},
        { key: "statut", label: t("saas.fournisseurs.col_statut"), render: (r) => (
          <span className="text-[10px] px-2 py-0.5 rounded-full font-medium uppercase tracking-wider"
            style={{ background: `${STATUT_COLORS[r.statut] || "#A1A1AA"}1A`, color: STATUT_COLORS[r.statut] || "#A1A1AA" }}>
            {t(`saas.fournisseurs.statuts.${r.statut}`, { defaultValue: r.statut })}
          </span>
        )},
        { key: "_relations", label: "", render: (r) => (
          <OntologyExplorerButton otype="supplier" oid={r.id} label={r.nom} rootLabel="Fournisseurs"
            className="btn-ghost text-[10px] px-2 py-1" />
        )},
      ]}
      emptyMsg={t("saas.fournisseurs.empty_msg")}
      primaryCta={t("saas.fournisseurs.primary_cta")}
      headerActions={<><ContextButton moduleKey="fournisseurs" /><ContextualImportButton moduleKey="fournisseurs" targetFields={FOURN_IMPORT_FIELDS} onImported={() => window.location.reload()} /></>}
      kpis={(data) => {
        const items = data.items || [];
        const total = items.length;
        const critiques = items.filter((i) => i.criticite === "critique").length;
        const contrat = items.reduce((s, i) => s + (i.contrat_eur || 0), 0);
        const score = items.length ? Math.round(items.reduce((s, i) => s + (i.score_risque || 0), 0) / items.length) : 0;
        return [
          { label: t("saas.fournisseurs.kpi_total"), value: total },
          { label: t("saas.fournisseurs.kpi_critique"), value: critiques, color: "#E24B4A" },
          { label: t("saas.fournisseurs.kpi_contrat"), value: formatEur(contrat), color: "#378ADD" },
          { label: t("saas.fournisseurs.kpi_score"), value: `${score}/100`, color: score >= 70 ? "#1D9E75" : score >= 50 ? "#EF9F27" : "#E24B4A" },
        ];
      }}
    />
    </>
  );
}
